// src/EmailSender.tsx

import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Typography,
  Paper,
  Box,
  Chip,
  Tooltip,
  Snackbar,
  Alert,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon,
  Preview as PreviewIcon
} from '@mui/icons-material';
import LoadingOverlay from './LoadingOverlay';
import EnhancedCSVImport from './components/EnhancedCSVImport';
import EnhancedCSVReview from './components/EnhancedCSVReview';
import TiptapEditor from './components/TiptapEditor';
import { EmailTemplate, Recipient, Campaign, AlertInfo } from './types';
import { generatePersonalizedEmail, generateEmailId } from './utils/templateUtils';
import { campaignApi, templateApi, emailConfigApi, accountTemplateApi, getDefaultAccountId } from './services/api';
import { v4 as uuidv4 } from 'uuid';
import ContactSelector from './components/ContactSelector';

interface EmailSenderProps {
  selectedTemplate?: EmailTemplate | null;
}

const EmailSender: React.FC<EmailSenderProps> = ({ selectedTemplate }) => {
  // Basic email fields
  const [emailAddresses, setEmailAddresses] = useState<string[]>(['']);
  const [isLoading, setIsLoading] = useState(false);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');

  // Template and personalization
  const [currentTemplate, setCurrentTemplate] = useState<EmailTemplate | null>(null);
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [campaignName, setCampaignName] = useState('');
  const [usePersonalization, setUsePersonalization] = useState(false);

  // UI state
  const [alertInfo, setAlertInfo] = useState<AlertInfo>({
    open: false,
    message: '',
    severity: 'info'
  });
  const [contactSelectorOpen, setContactSelectorOpen] = useState(false);
  const [csvReviewOpen, setCsvReviewOpen] = useState(false);
  const [pendingRecipients, setPendingRecipients] = useState<Recipient[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [accountTemplate, setAccountTemplate] = useState<any>(null);
  const [templatedPreviewUrl, setTemplatedPreviewUrl] = useState<string>('');
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [emailConfigs, setEmailConfigs] = useState<any[]>([]);
  const [selectedEmailConfig, setSelectedEmailConfig] = useState<string>('');
  const [configValidationStatus, setConfigValidationStatus] = useState<{[key: string]: boolean | null}>({});
  const [editingCampaignId, setEditingCampaignId] = useState<string | null>(null);

  // Load templates and email configs on mount
  useEffect(() => {
    loadTemplatesAndConfigs();

    // Check if we're editing an existing campaign
    const editCampaignData = localStorage.getItem('editCampaign');
    if (editCampaignData) {
      try {
        const campaign = JSON.parse(editCampaignData);
        // Populate form with campaign data
        setEditingCampaignId(campaign.id);
        setCampaignName(campaign.name);
        setSubject(campaign.subject);
        setMessage(campaign.content);
        setSelectedEmailConfig(campaign.emailConfigId);

        // Set recipients
        if (campaign.recipients && campaign.recipients.length > 0) {
          const recipientEmails = campaign.recipients.map((r: any) => r.email || r);
          setEmailAddresses(recipientEmails);
          setRecipients(campaign.recipients.map((r: any) => ({
            email: r.email || r,
            variables: r.variables || {},
            id: r.id || generateEmailId()
          })));
        }

        // Set template if available
        if (campaign.templateId) {
          // Template will be set when templates are loaded
          setTimeout(() => {
            const template = templates.find(t => t.id === campaign.templateId);
            if (template) {
              setCurrentTemplate(template);
              setUsePersonalization(true);
            }
          }, 100);
        }

        // Clear the localStorage item after loading
        localStorage.removeItem('editCampaign');
      } catch (error) {
        console.error('Error loading campaign for editing:', error);
      }
    }
  }, []);

  // Effect to apply selected template
  useEffect(() => {
    if (selectedTemplate) {
      setCurrentTemplate(selectedTemplate);
      setSubject(selectedTemplate.subject);
      setMessage(selectedTemplate.content);
      setUsePersonalization(selectedTemplate.variables.length > 0);
      setCampaignName(`${selectedTemplate.name} Campaign`);
    }
  }, [selectedTemplate]);

  const loadTemplatesAndConfigs = async () => {
    try {
      const accountId = getDefaultAccountId();

      // Load templates
      const templatesResponse = await templateApi.getByAccount(accountId);
      setTemplates(templatesResponse.templates || []);

      // Load email configs
      const configsResponse = await emailConfigApi.getByAccount(accountId);
      setEmailConfigs(configsResponse.configs || []);

      // Load account template
      try {
        const accountTemplateResponse = await accountTemplateApi.getAccountTemplate(accountId);
        if (accountTemplateResponse.template) {
          setAccountTemplate(accountTemplateResponse.template);
          console.log('Loaded account template:', accountTemplateResponse.template);
        }
      } catch (error) {
        console.log('No account template found or error loading:', error);
        setAccountTemplate(null);
      }

      // Initialize validation status for all configs
      const validationStatus: {[key: string]: boolean | null} = {};
      configsResponse.configs?.forEach((config: any) => {
        validationStatus[config.id] = config.isValid;
      });
      setConfigValidationStatus(validationStatus);

      // Set default email config
      const defaultConfig = configsResponse.configs?.find((c: any) => c.isDefault);
      if (defaultConfig) {
        setSelectedEmailConfig(defaultConfig.id);
        // Validate the default config if it hasn't been tested recently
        if (defaultConfig.isValid !== true) {
          validateEmailConfig(defaultConfig.id);
        }
      }
    } catch (error) {
      console.error('Error loading templates and configs:', error);
      showAlert('Failed to load templates and email configurations', 'error');
    }
  };

  const validateEmailConfig = async (configId: string) => {
    try {
      const result = await emailConfigApi.validateConfig(configId);
      setConfigValidationStatus(prev => ({
        ...prev,
        [configId]: result.success
      }));

      if (!result.success) {
        showAlert(`Email configuration validation failed: ${result.message}`, 'error');
      }
    } catch (error) {
      console.error('Error validating email config:', error);
      setConfigValidationStatus(prev => ({
        ...prev,
        [configId]: false
      }));
      showAlert('Failed to validate email configuration', 'error');
    }
  };

  const handleEmailConfigChange = async (configId: string) => {
    setSelectedEmailConfig(configId);

    // Validate the selected config
    if (configId && configValidationStatus[configId] !== true) {
      await validateEmailConfig(configId);
    }
  };

  const validateEmailContent = () => {
    const errors: string[] = [];

    // Check if subject is empty
    if (!subject.trim()) {
      errors.push("Subject is required");
    }

    // Check if message content is empty
    if (!message.trim()) {
      errors.push("Message content is required");
    }

    // Check for empty HTML content (just tags without text)
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = message;
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    if (!textContent.trim()) {
      errors.push("Message content appears to be empty");
    }

    return errors;
  };

  const validateRecipientsAndVariables = () => {
    const errors: string[] = [];

    if (usePersonalization && currentTemplate) {
      const requiredVariables = currentTemplate.variables || [];

      if (requiredVariables.length > 0) {
        const recipientsWithMissingVars = recipients.filter(recipient => {
          const missingVars = requiredVariables.filter(variable =>
            !recipient.variables[variable] ||
            recipient.variables[variable].toString().trim() === ''
          );
          return missingVars.length > 0;
        });

        if (recipientsWithMissingVars.length > 0) {
          errors.push(`${recipientsWithMissingVars.length} recipient(s) are missing required template variables`);

          // Show details for first few recipients
          const detailsToShow = recipientsWithMissingVars.slice(0, 3);
          detailsToShow.forEach(recipient => {
            const missingVars = requiredVariables.filter(variable =>
              !recipient.variables[variable] ||
              recipient.variables[variable].toString().trim() === ''
            );
            errors.push(`• ${recipient.email}: missing ${missingVars.join(', ')}`);
          });

          if (recipientsWithMissingVars.length > 3) {
            errors.push(`• ... and ${recipientsWithMissingVars.length - 3} more recipients`);
          }
        }
      }

      // Check for recipients with invalid email addresses
      const invalidEmails = recipients.filter(recipient => !isValidEmail(recipient.email));
      if (invalidEmails.length > 0) {
        errors.push(`${invalidEmails.length} recipient(s) have invalid email addresses`);
      }
    } else {
      // Simple email mode validation
      const validEmails = emailAddresses.filter(isValidEmail);
      if (validEmails.length === 0) {
        errors.push("At least one valid email address is required");
      }
    }

    return errors;
  };

  const validateCampaignReadiness = () => {
    const errors: string[] = [];

    // Email configuration validation
    if (!selectedEmailConfig || emailConfigs.length === 0) {
      errors.push("Please select an email configuration");
    } else if (configValidationStatus[selectedEmailConfig] === false) {
      errors.push("Selected email configuration is invalid");
    } else if (configValidationStatus[selectedEmailConfig] === null) {
      errors.push("Email configuration needs to be tested");
    }

    // Content validation
    errors.push(...validateEmailContent());

    // Recipients and variables validation
    errors.push(...validateRecipientsAndVariables());

    // Campaign name validation for personalized campaigns
    if (usePersonalization && !campaignName.trim()) {
      errors.push("Campaign name is required for personalized emails");
    }

    return errors;
  };

  const handleEmailChange = (index: number, value: string) => {
    const updatedEmails = [...emailAddresses];
    updatedEmails[index] = value;
    setEmailAddresses(updatedEmails);
  };

  const handleDeleteEmail = (emailToDelete: string) => {
    setEmailAddresses((prev) => prev.filter((email) => email !== emailToDelete));
  };

  const handleAddEmail = () => {
    if (isValidEmail(emailAddresses[0]))
      setEmailAddresses(['', ...emailAddresses]);
  };

  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleRecipientsImported = (importedRecipients: Recipient[]) => {
    setPendingRecipients(importedRecipients);
    setCsvReviewOpen(true);
  };

  const handleCSVReviewConfirm = (confirmedRecipients: Recipient[]) => {
    setRecipients(prev => [...prev, ...confirmedRecipients]);
    setUsePersonalization(true);
    showAlert(`Added ${confirmedRecipients.length} recipients`, 'success');
    setPendingRecipients([]);
  };

  const handleContactsSelected = (selectedContacts: Recipient[]) => {
    setRecipients(prev => [...prev, ...selectedContacts]);
    setUsePersonalization(true);
    showAlert(`Added ${selectedContacts.length} contacts`, 'success');
  };

  const handleTemplateSelect = (templateId: string) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setCurrentTemplate(template);
      setSubject(template.subject);
      setMessage(template.content);
      setUsePersonalization(template.variables.length > 0);
    }
  };

  const handleClearTemplate = () => {
    setCurrentTemplate(null);
    setSubject('');
    setMessage('');
  };

  const generateTemplatedPreview = async () => {
    if (!accountTemplate || !accountTemplate.designType) {
      return null;
    }

    try {
      const response = await accountTemplateApi.generatePreview({
        designType: accountTemplate.designType,
        logoUrl: accountTemplate.logoUrl,
        companyName: accountTemplate.companyName,
        termsUrl: accountTemplate.termsUrl,
        privacyUrl: accountTemplate.privacyUrl,
        baseColor: accountTemplate.baseColor,
        accentColor: accountTemplate.accentColor,
        subject: subject || 'Sample Email Subject',
        content: message || '<p>Your email content will appear here</p>'
      });

      const blob = response;
      const url = URL.createObjectURL(blob);
      setTemplatedPreviewUrl(url);
      return url;
    } catch (error) {
      console.error('Error generating templated preview:', error);
      return null;
    }
  };

  const handleDeleteRecipient = (email: string) => {
    setRecipients(prev => prev.filter(r => r.email !== email));
  };

  const handleSaveDraft = async () => {
    // Determine recipients based on personalization mode
    let campaignRecipients: Recipient[] = [];

    if (usePersonalization && recipients.length > 0) {
      campaignRecipients = recipients;
    } else {
      const validEmails = emailAddresses.filter(isValidEmail);
      campaignRecipients = validEmails.map(email => ({
        email,
        variables: {},
        id: generateEmailId()
      }));
    }

    // Basic validation for draft
    if (!campaignName.trim()) {
      showAlert("Add campaign name to save as draft", "info");
      return;
    }

    setIsLoading(true);

    try {
      const campaignId = editingCampaignId || uuidv4();

      // Create draft campaign object
      const campaign: Campaign = {
        id: campaignId,
        name: campaignName,
        templateId: currentTemplate?.id || '',
        recipients: campaignRecipients,
        senderEmail: emailConfigs.find(c => c.id === selectedEmailConfig)?.fromEmail || '',
        senderName: emailConfigs.find(c => c.id === selectedEmailConfig)?.fromName || '',
        emailConfigId: selectedEmailConfig,
        domain: emailConfigs.find(c => c.id === selectedEmailConfig)?.smtpHost || '',
        status: 'DRAFT',
        createdAt: new Date(),
        analytics: {
          totalSent: 0,
          totalOpened: 0,
          totalClicked: 0,
          openRate: 0,
          clickRate: 0,
          bounced: 0,
          unsubscribed: 0
        }
      };



      // Save or update draft campaign to API
      if (editingCampaignId) {
        await campaignApi.update(editingCampaignId, {
          name: campaignName,
          subject: subject,
          content: message,
          templateId: currentTemplate?.id || null,
          emailConfigId: selectedEmailConfig,
          accountId: getDefaultAccountId(),
          recipients: campaignRecipients
        });
        showAlert("Campaign updated successfully", "success");
      } else {
        await campaignApi.create({
          ...campaign,
          accountId: getDefaultAccountId(),
          recipients: campaignRecipients
        });
        showAlert("Campaign saved as draft successfully", "success");
      }

      // Reset form
      setSubject('');
      setMessage('');
      setEmailAddresses(['']);
      setRecipients([]);
      setCampaignName('');
      setCurrentTemplate(null);
      setUsePersonalization(false);
      setEditingCampaignId(null);

    } catch (error) {
      console.error('Failed to save draft:', error);
      showAlert("Failed to save draft", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = async () => {
    // Comprehensive validation
    const validationErrors = validateCampaignReadiness();

    if (validationErrors.length > 0) {
      const errorMessage = validationErrors.join('\n');
      showAlert(`Please fix the following issues before sending:\n\n${errorMessage}`, "error");
      return;
    }

    // Determine recipients based on personalization mode
    let finalRecipients: string[] = [];
    let campaignRecipients: Recipient[] = [];

    if (usePersonalization && recipients.length > 0) {
      finalRecipients = recipients.map(r => r.email);
      campaignRecipients = recipients;
    } else {
      const validEmails = emailAddresses.filter(isValidEmail);
      finalRecipients = validEmails;
      campaignRecipients = validEmails.map(email => ({
        email,
        variables: {},
        id: generateEmailId()
      }));
    }

    // Additional real-time validation for config status
    if (configValidationStatus[selectedEmailConfig] === null) {
      showAlert("Testing email configuration...", "info");
      await validateEmailConfig(selectedEmailConfig);
      if (configValidationStatus[selectedEmailConfig] === false) {
        showAlert("Email configuration validation failed. Please choose a different configuration.", "error");
        return;
      }
    }

    setIsLoading(true);

    try {
      const campaignId = uuidv4();

      // Create campaign object
      const campaign: Campaign = {
        id: campaignId,
        name: campaignName,
        templateId: currentTemplate?.id || '',
        recipients: campaignRecipients,
        senderEmail: emailConfigs.find(c => c.id === selectedEmailConfig)?.fromEmail || '',
        senderName: emailConfigs.find(c => c.id === selectedEmailConfig)?.fromName || '',
        domain: emailConfigs.find(c => c.id === selectedEmailConfig)?.smtpHost || '',
        emailConfigId: selectedEmailConfig,
        status: 'SENDING',
        createdAt: new Date(),
        analytics: {
          totalSent: 0,
          totalOpened: 0,
          totalClicked: 0,
          openRate: 0,
          clickRate: 0,
          bounced: 0,
          unsubscribed: 0
        }
      };

      // Prepare personalized emails if using templates
      const emailsToSend = [];

      if (usePersonalization && currentTemplate) {
        for (const recipient of campaignRecipients) {
          const personalized = generatePersonalizedEmail(currentTemplate, recipient);
          if (personalized.isValid) {
            const emailId = generateEmailId();

            emailsToSend.push({
              to: recipient.email,
              subject: personalized.subject,
              content: personalized.content,
              emailId
            });
          }
        }
      } else {
        // Simple email without personalization
        for (const email of finalRecipients) {
          const emailId = generateEmailId();

          emailsToSend.push({
            to: email,
            subject,
            content: message,
            emailId
          });
        }
      }

      // Send emails via API
      const emailData = {
        campaignName: campaignName,
        emailConfigId: selectedEmailConfig,
        from: emailConfigs.find(c => c.id === selectedEmailConfig)?.fromEmail || '', // Keep for backward compatibility
        to: finalRecipients,
        senderName: emailConfigs.find(c => c.id === selectedEmailConfig)?.fromName || '',
        subject: subject,
        message: message,
        campaignId,
        trackingEnabled: true,
        personalizedEmails: emailsToSend,
        accountId: getDefaultAccountId() // Add accountId for AccountTemplate lookup
      };

      const result = await campaignApi.sendEmails(emailData);
      console.log('Email sent successfully:', result);

      // Update campaign status
      campaign.status = 'SENT';
      campaign.sentAt = new Date();
      campaign.analytics.totalSent = finalRecipients.length;

      // Save campaign to API
      try {
        await campaignApi.create(campaign);
      } catch (error) {
        console.error('Error saving campaign:', error);
        // Continue even if campaign save fails, as email was sent
      }

      // Reset form
      setSubject('');
      setMessage('');
      setEmailAddresses(['']);
      setRecipients([]);
      setCampaignName('');
      setCurrentTemplate(null);
      setUsePersonalization(false);

      showAlert(`Campaign sent successfully to ${finalRecipients.length} recipients`, "success");

    } catch (error) {
      console.error('Failed to send email:', error);
      showAlert("Something went wrong", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault(); // Prevent the default paste behavior
    const pastedData = event.clipboardData.getData('text');
    const emails = pastedData.split(/[\s,]+/).filter(Boolean); // Split by whitespace or comma

    setEmailAddresses((prev) => {
      const updatedEmails = [...prev];

      emails.forEach((email) => {
        const trimmedEmail = email.trim();
        if (isValidEmail(trimmedEmail) && !updatedEmails.includes(trimmedEmail)) {
          updatedEmails.push(trimmedEmail); // Add valid and unique email
        }
      });

      return updatedEmails;
    });
  };

    const showAlert = (message: string, severity: 'info' | 'success' | 'warning' | 'error') => {
        setAlertInfo({ open: true, message, severity });
        setTimeout(() => {
          handleCloseAlert();
        }, 6000);
    };

    const handleCloseAlert = () => {
        setAlertInfo({ ...alertInfo, open: false });
    };

  return (
    <>
      <LoadingOverlay isLoading={isLoading} />
      <Snackbar open={alertInfo.open} autoHideDuration={6000} onClose={handleCloseAlert}>
        <Alert severity={alertInfo.severity}>
          {alertInfo.message}
        </Alert>
      </Snackbar>

      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Fixed Header */}
        <Box sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: 'white',
          borderBottom: '1px solid',
          borderColor: 'divider',
          pb: 2,
          mb: 3
        }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
              {editingCampaignId ? 'Edit Email Campaign' : 'Compose Email Campaign'}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {editingCampaignId ? 'Update your email campaign' : 'Create and send personalized email campaigns'}
            </Typography>
          </Box>
        </Box>

        {/* Scrollable Content */}
        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>

          {/* Campaign Settings */}
          <Card sx={{ mb: 3, borderRadius: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Campaign Settings
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Campaign Name"
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                  placeholder="Enter campaign name"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Email Mode</InputLabel>
                  <Select
                    value={usePersonalization ? 'personalized' : 'simple'}
                    onChange={(e) => setUsePersonalization(e.target.value === 'personalized')}
                    label="Email Mode"
                  >
                    <MenuItem value="simple">Simple Email</MenuItem>
                    <MenuItem value="personalized">Personalized Campaign</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
          </Card>
          {/* Sender Configuration */}
          <Card sx={{ mb: 3, borderRadius: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Sender Configuration
            </Typography>

            {emailConfigs.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  No email configurations found
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  You need to set up at least one email configuration before sending campaigns.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    showAlert('Please go to Email Configurations in the sidebar to set up your sender details.', 'warning');
                  }}
                  sx={{ borderRadius: 2 }}
                >
                  Set Up Email Configuration
                </Button>
              </Box>
            ) : (
              <FormControl fullWidth>
                <InputLabel>Email Configuration</InputLabel>
                <Select
                  value={selectedEmailConfig}
                  onChange={(e) => handleEmailConfigChange(e.target.value)}
                  label="Email Configuration"
                >
                  {emailConfigs.map((config) => (
                    <MenuItem key={config.id} value={config.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {config.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {config.fromEmail} • {config.smtpHost}
                          </Typography>
                        </Box>
                        <Box>
                          {configValidationStatus[config.id] === true && (
                            <Chip label="✓" color="success" size="small" />
                          )}
                          {configValidationStatus[config.id] === false && (
                            <Chip label="⚠" color="error" size="small" />
                          )}
                          {configValidationStatus[config.id] === null && (
                            <Chip label="?" color="warning" size="small" />
                          )}
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </CardContent>
          </Card>

          {/* Email Content */}
          <Card sx={{ mb: 3, borderRadius: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">
                Email Content
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<PreviewIcon />}
                  onClick={async () => {
                    const contentErrors = validateEmailContent();
                    if (contentErrors.length > 0) {
                      showAlert(`Cannot preview: ${contentErrors.join(', ')}`, 'warning');
                      return;
                    }

                    // Generate templated preview if account template exists
                    if (accountTemplate && accountTemplate.designType) {
                      await generateTemplatedPreview();
                    }

                    setPreviewOpen(true);
                  }}
                  disabled={!subject.trim() || !message.trim()}
                  sx={{ borderRadius: 2 }}
                >
                  Preview
                </Button>
              </Box>
            </Box>

            {/* Template Selection */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Template Selection
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel>Choose Template</InputLabel>
                  <Select
                    value={currentTemplate?.id || ''}
                    onChange={(e) => handleTemplateSelect(e.target.value)}
                    label="Choose Template"
                  >
                    <MenuItem value="">
                      <em>No Template (Custom Email)</em>
                    </MenuItem>
                    {templates.map((template) => (
                      <MenuItem key={template.id} value={template.id}>
                        {template.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {currentTemplate && (
                  <Button
                    variant="outlined"
                    onClick={handleClearTemplate}
                    sx={{ borderRadius: 2 }}
                  >
                    Clear Template
                  </Button>
                )}
              </Box>

              {currentTemplate && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Using template: <strong>{currentTemplate.name}</strong>
                  {currentTemplate.variables.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      Variables: {currentTemplate.variables.map(v => (
                        <Chip key={v} label={v} size="small" sx={{ mr: 0.5 }} />
                      ))}
                    </Box>
                  )}
                </Alert>
              )}
            </Box>

            <TextField
              fullWidth
              label="Subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter email subject"
              sx={{ mb: 2 }}
            />

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Message Content
              </Typography>
              <TiptapEditor
                content={message}
                onChange={setMessage}
                placeholder={usePersonalization ? "Use {{variableName}} for personalization" : "Enter your message"}
              />
            </Box>
          </CardContent>
          </Card>

          {/* Recipients Section */}
          <Card sx={{ mb: 3, borderRadius: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Recipients
            </Typography>

            {usePersonalization ? (
              <Box>
                {/* Contact Selection and CSV Import Options */}
                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                  <Button
                    variant="outlined"
                    startIcon={<UploadIcon />}
                    onClick={() => setContactSelectorOpen(true)}
                    sx={{ borderRadius: 2 }}
                  >
                    Select from Contacts
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<UploadIcon />}
                    sx={{ borderRadius: 2 }}
                  >
                    Import CSV
                  </Button>
                </Box>

                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>
                      Import Recipients from CSV
                      {currentTemplate && (
                        <Chip
                          label={`Template: ${currentTemplate.name}`}
                          size="small"
                          sx={{ ml: 2 }}
                          color="primary"
                        />
                      )}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <EnhancedCSVImport
                      template={currentTemplate}
                      onRecipientsImported={handleRecipientsImported}
                      showAlert={showAlert}
                    />
                  </AccordionDetails>
                </Accordion>

                {recipients.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Imported Recipients ({recipients.length})
                    </Typography>
                    {currentTemplate && currentTemplate.variables.length > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                        Required variables: {currentTemplate.variables.join(', ')}
                      </Typography>
                    )}
                    <List dense>
                      {recipients.slice(0, 5).map((recipient) => {
                        const missingVars = currentTemplate?.variables.filter(variable =>
                          !recipient.variables[variable] ||
                          recipient.variables[variable].toString().trim() === ''
                        ) || [];
                        const hasIssues = missingVars.length > 0 || !isValidEmail(recipient.email);

                        return (
                          <ListItem
                            key={recipient.email}
                            sx={{
                              bgcolor: hasIssues ? 'error.light' : 'transparent',
                              borderRadius: 1,
                              mb: 0.5
                            }}
                            secondaryAction={
                              <IconButton onClick={() => handleDeleteRecipient(recipient.email)}>
                                <DeleteIcon />
                              </IconButton>
                            }
                          >
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {recipient.email}
                                  {!isValidEmail(recipient.email) && (
                                    <Chip label="Invalid Email" color="error" size="small" />
                                  )}
                                  {missingVars.length > 0 && (
                                    <Chip
                                      label={`Missing: ${missingVars.join(', ')}`}
                                      color="warning"
                                      size="small"
                                    />
                                  )}
                                </Box>
                              }
                              secondary={Object.keys(recipient.variables).length > 0
                                ? `Variables: ${Object.keys(recipient.variables).join(', ')}`
                                : 'No variables'
                              }
                            />
                          </ListItem>
                        );
                      })}
                      {recipients.length > 5 && (
                        <ListItem>
                          <ListItemText secondary={`... and ${recipients.length - 5} more`} />
                        </ListItem>
                      )}
                    </List>
                  </Box>
                )}
              </Box>
            ) : (
              <Box>
                <TextField
                  fullWidth
                  value={emailAddresses[0]}
                  onChange={(e) => handleEmailChange(0, e.target.value)}
                  onPaste={handlePaste}
                  placeholder="Enter email address"
                  type="email"
                  sx={{ mb: 2 }}
                />

                {emailAddresses.slice(1).map((email, index) => (
                  <Tooltip title={email} key={email}>
                    <Chip
                      sx={{ m: 0.5 }}
                      label={email}
                      onDelete={() => handleDeleteEmail(email)}
                    />
                  </Tooltip>
                ))}

                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={handleAddEmail}
                    disabled={!isValidEmail(emailAddresses[0])}
                  >
                    Add Email
                  </Button>
                </Box>
              </Box>
            )}
          </CardContent>
          </Card>
          
          {/* Send Button */}
          <Card sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 3 }}>
            {(() => {
              const validationErrors = validateCampaignReadiness();
              const hasErrors = validationErrors.length > 0;

              return (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6">
                        Ready to Send
                      </Typography>

                      {hasErrors ? (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2" color="error" sx={{ fontWeight: 500 }}>
                            ❌ Campaign has validation issues:
                          </Typography>
                          <Box component="ul" sx={{ mt: 1, pl: 2, mb: 0 }}>
                            {validationErrors.slice(0, 3).map((error, index) => (
                              <Typography
                                key={index}
                                component="li"
                                variant="caption"
                                color="error"
                                sx={{ display: 'list-item', mb: 0.5 }}
                              >
                                {error}
                              </Typography>
                            ))}
                            {validationErrors.length > 3 && (
                              <Typography
                                component="li"
                                variant="caption"
                                color="error"
                                sx={{ display: 'list-item' }}
                              >
                                ... and {validationErrors.length - 3} more issues
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                          {usePersonalization && recipients.length > 0 ? (
                            `✅ ${recipients.length} personalized emails ready to send`
                          ) : (
                            `✅ ${emailAddresses.filter(isValidEmail).length} recipients ready to send`
                          )}
                        </Typography>
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Button
                        variant="outlined"
                        onClick={handleSaveDraft}
                        size="large"
                        disabled={!campaignName.trim()}
                        sx={{ borderRadius: 2 }}
                      >
                        {editingCampaignId ? 'Update Campaign' : 'Save as Draft'}
                      </Button>
                      <Button
                        variant="contained"
                        color="success"
                        onClick={handleSend}
                        size="large"
                        disabled={hasErrors}
                        sx={{ borderRadius: 2 }}
                      >
                        Send Campaign
                      </Button>
                    </Box>
                  </Box>
                </>
              );
            })()}
          </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Contact Selector Dialog */}
      <ContactSelector
        open={contactSelectorOpen}
        onClose={() => setContactSelectorOpen(false)}
        onContactsSelected={handleContactsSelected}
        showAlert={showAlert}
      />

      {/* Enhanced CSV Review Dialog */}
      <EnhancedCSVReview
        open={csvReviewOpen}
        onClose={() => {
          setCsvReviewOpen(false);
          setPendingRecipients([]);
        }}
        contacts={pendingRecipients.map(r => ({
          email: r.email,
          variables: r.variables,
          isValid: true,
          exists: false,
          issues: [],
          missingVariables: currentTemplate?.variables.filter(v => !r.variables[v]) || []
        }))}
        template={currentTemplate}
        onContactsConfirmed={(contacts, updateExisting) => {
          const recipients = contacts.map(contact => ({
            email: contact.email,
            variables: contact.variables,
            id: `${Date.now()}_${Math.random()}`
          }));
          handleCSVReviewConfirm(recipients);
        }}
        showAlert={showAlert}
      />

      {/* Email Preview Dialog */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Email Preview
          {accountTemplate && accountTemplate.designType && (
            <Chip
              label={`Using ${accountTemplate.designType} template`}
              color="primary"
              size="small"
              sx={{ ml: 2 }}
            />
          )}
        </DialogTitle>
        <DialogContent>
          {accountTemplate && accountTemplate.designType && templatedPreviewUrl ? (
            // Show templated preview
            <Box>
              <Typography variant="subtitle2" gutterBottom color="primary">
                Templated Preview (with {accountTemplate.designType} design):
              </Typography>
              <Box
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  overflow: 'hidden',
                  mb: 2
                }}
              >
                <iframe
                  src={templatedPreviewUrl}
                  width="100%"
                  height="600px"
                  style={{ border: 'none' }}
                  title="Templated Email Preview"
                />
              </Box>
              <Typography variant="caption" color="text.secondary">
                This preview shows how your email will look with your account template design, colors, and branding.
              </Typography>
            </Box>
          ) : (
            // Show basic preview
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Subject:
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>
                {subject || 'No subject'}
              </Typography>

              <Typography variant="subtitle2" gutterBottom>
                Message:
              </Typography>
              <Paper sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                <div dangerouslySetInnerHTML={{ __html: message || 'No content' }} />
              </Paper>

              {!accountTemplate?.designType && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                  No account template selected. Email will be sent without template wrapper.
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EmailSender;
